import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { Dialog, DialogHeader, DialogTitle, DialogContent } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import {
  ArrowLeft,
  FileText,
  Loader2,
  CheckCircle2,
  FileSignature,
  Trash2,
  Plus,
  Maximize2,
  Save,
  AlertCircle
} from 'lucide-react';
import { SignatureDialog } from '../../../../../components/ui/SignatureDialog';
import { useTeamMembers } from '../../../../../redux/ProductionData/hooks/useTeamMembers';
import { useReportDraft } from '../../../../../redux/ReportDrafts/hooks/useReportDraft';
import { REPORT_TYPES } from '../../../../../redux/ReportDrafts/types';
import { cn } from '../../../../../lib/utils';
import { generateMaterialRegistryReport, clearMaterialRegistryReport } from '../../../../../redux/Project/materialsData/action';
import { useDispatch, useSelector } from 'react-redux';
import { PdfViewer } from '../../../../../components/viewers/PdfViewer';
import { Notification as customNotification } from '../../../../../components/ui/custom-notification';

export interface ConformityReportFormPayload {
  reportType: string;
  projectId: number | string;
  projectName: string;
  projectNumber: string;
  header: {
    conformityReportNo: string;
    clientName: string;
    clientPurchaseOrderNo: string;
    issuerCompany: string;
  };
  covering: string;
  certifiesStatement: string;
  exceptions: string;
  rows: ConformityProcessRow[];
  signatures: {
    projectRepresentative: FinalSignature | null;
    qaSignatory: FinalSignature | null;
  };
  selectedSigners: {
    projectRepresentativeMemberId: string;
    qaMemberId: string;
  };
}

interface FinalSignature {
  signature: string;
  name: string;
  timestamp: string;
  comments: string;
  stampImage: string;
}

export interface ConformityProcessRow {
  id: string;
  description: string;
}

interface GenerateConformityReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number | string;
  projectName: string;
  jobNumber: string;
}

const CONFORMITY_DRAFT_REPORT_TYPE = REPORT_TYPES.CERTIFICATE_OF_CONFORMITY_REPORT;

const DEFAULT_HEADER = {
  conformityReportNo: '',
  clientName: 'Enbridge Offshore Facilities LLC',
  clientPurchaseOrderNo: '129010019',
  issuerCompany: 'Pipeline Technique Limited'
};

const DEFAULT_COVERING =
  'Provision of services in compliance with Enbridge Projects Offshore Pipeline Welding/NDE Document# MPH-PPL-SPC-ENB-0001 Revision 1, for the welding of the Buckle Arrestor and Transition Joint Welding (so called the 1st) Enbridge project in terms of the following:';

const DEFAULT_CERTIFIES =
  'This CERTIFIES that the equipment and/or services identified by and provided under the above references, CONFORMS TO THE SPECIFIC REQUIREMENTS, save only the exceptions listed below, of the Order or Contract, including all provisions as to design, performance, construction, material and workmanship.';

const DEFAULT_EXCEPTIONS = 'All work performed by UT Quality was free issued by COMPANY.';

const DEFAULT_ROW_DESCRIPTIONS: string[] = [
  'Procedure Qualification, Welder Qualification, Production Welding & Coating of 10.75" x 0.5" Buckle Arrestors',
  'Procedure Qualification, Welder Qualification, Production Welding & Coating of 18" x 0.875" Buckle Arrestors',
  'Procedure Qualification, Welder Qualification, Production Welding & Coating of 18.75" x 0.75" Buckle Arrestors',
  'Procedure Qualification, Welder Qualification, Production Welding & Coating of 10.75" x 0.594 Pipeline to SCR Transition Joint',
  'Procedure Qualification, Welder Qualification, Production Welding & Coating of 16" x 1" to 18" x 0.875" Pipeline to SCR Transition Joint'
];

const newRowId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const mapDefaultsToRows = (): ConformityProcessRow[] =>
  DEFAULT_ROW_DESCRIPTIONS.map((description) => ({
    id: newRowId(),
    description
  }));

/** Migrate older drafts that stored checkbox columns. */
const normalizeDraftRows = (raw: unknown): ConformityProcessRow[] => {
  if (!Array.isArray(raw) || raw.length === 0) return mapDefaultsToRows();
  return raw.map((r: { id?: string; description?: string }) => ({
    id: typeof r?.id === 'string' && r.id ? r.id : newRowId(),
    description: typeof r?.description === 'string' ? r.description : ''
  }));
};

export const GenerateConformityReportModal: React.FC<GenerateConformityReportModalProps> = ({
  isOpen,
  onClose,
  projectId,
  projectName,
  jobNumber,
}) => {
  const numericProjectId = useMemo(() => {
    const n = typeof projectId === 'string' ? parseInt(projectId, 10) : Number(projectId);
    return Number.isFinite(n) ? n : 0;
  }, [projectId]);

  const { teamMembers, loadTeamMembers } = useTeamMembers();
  const dispatch = useDispatch();
  const {
    materialRegistryReportLoading,
    materialRegistryReportSuccess,
    materialRegistryReportError,
    materialRegistryReportData
  } = useSelector((state: any) => state.materialsData || {});
  const {
    draft,
    draftSignatures,
    draftId,
    updatedAt,
    isFetchingDraft,
    isSavingDraft,
    saveDraftSuccess,
    saveDraftError,
    fetchDraftError,
    saveDraft,
    loadDraft,
    clearDraft,
    clearSaveStatus
  } = useReportDraft();

  const [headerData, setHeaderData] = useState({ ...DEFAULT_HEADER });

  const [covering, setCovering] = useState(DEFAULT_COVERING);
  const [certifiesStatement, setCertifiesStatement] = useState(DEFAULT_CERTIFIES);
  const [exceptions, setExceptions] = useState(DEFAULT_EXCEPTIONS);
  const [rows, setRows] = useState<ConformityProcessRow[]>(mapDefaultsToRows);

  const [repList, setRepList] = useState<Array<{ value: string; label: string; name: string }>>([]);
  const [qaList, setQaList] = useState<Array<{ value: string; label: string; name: string }>>([]);
  const [selectedRep, setSelectedRep] = useState('');
  const [selectedQa, setSelectedQa] = useState('');

  const [projectRepSignature, setProjectRepSignature] = useState<FinalSignature | null>(null);
  const [qaSignature, setQaSignature] = useState<FinalSignature | null>(null);

  const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);
  const [signatureContext, setSignatureContext] = useState<'rep' | 'qa' | null>(null);

  const [fullViewOpen, setFullViewOpen] = useState(false);
  const [viewerFile, setViewerFile] = useState<{ fileUrl: string; fileName: string } | null>(null);

  const isApplyingDraftRef = useRef(false);
  const lastSignerRestoreKeyRef = useRef('');

  useEffect(() => {
    if (isOpen && projectId) {
      loadTeamMembers(projectId);
    }
  }, [isOpen, projectId, loadTeamMembers]);

  useEffect(() => {
    dispatch(clearMaterialRegistryReport());
    return () => {
      dispatch(clearMaterialRegistryReport());
    };
  }, [dispatch]);

  useEffect(() => {
    if (isOpen && materialRegistryReportSuccess && materialRegistryReportData) {
      const fileUrl =
        materialRegistryReportData?.downloadUrl ||
        materialRegistryReportData?.data?.downloadUrl ||
        materialRegistryReportData?.fileUrl ||
        materialRegistryReportData?.data?.fileUrl;
      const fileName =
        materialRegistryReportData?.fileName ||
        materialRegistryReportData?.data?.fileName ||
        (headerData.conformityReportNo?.trim()
          ? `Conformity Report ${headerData.conformityReportNo.trim()}`
          : 'Certificate of Conformity Report');
      if (fileUrl) {
        setViewerFile({ fileUrl, fileName });
      }
    }
  }, [
    isOpen,
    materialRegistryReportSuccess,
    materialRegistryReportData,
    headerData.conformityReportNo
  ]);

  useLayoutEffect(() => {
    if (!isOpen || !numericProjectId) return;
    clearDraft();
    loadDraft({
      projectId: numericProjectId,
      reportType: CONFORMITY_DRAFT_REPORT_TYPE
    });
  }, [isOpen, numericProjectId, loadDraft, clearDraft]);

  useEffect(() => {
    if (isOpen) {
      clearSaveStatus();
    }
  }, [isOpen, clearSaveStatus]);

  useEffect(() => {
    if (!isOpen) {
      setViewerFile(null);
      dispatch(clearMaterialRegistryReport());
      setFullViewOpen(false);
      setIsSignatureDialogOpen(false);
      setSignatureContext(null);
      setHeaderData({ ...DEFAULT_HEADER });
      setCovering(DEFAULT_COVERING);
      setCertifiesStatement(DEFAULT_CERTIFIES);
      setExceptions(DEFAULT_EXCEPTIONS);
      setRows(mapDefaultsToRows());
      setSelectedRep('');
      setSelectedQa('');
      setProjectRepSignature(null);
      setQaSignature(null);
      lastSignerRestoreKeyRef.current = '';
      clearDraft();
      clearSaveStatus();
    }
  }, [isOpen, clearDraft, clearSaveStatus, dispatch]);

  useEffect(() => {
    if (!isOpen || isFetchingDraft) return;
    if (draft != null) return;
    setHeaderData({ ...DEFAULT_HEADER });
    setCovering(DEFAULT_COVERING);
    setCertifiesStatement(DEFAULT_CERTIFIES);
    setExceptions(DEFAULT_EXCEPTIONS);
    setRows(mapDefaultsToRows());
    setSelectedRep('');
    setSelectedQa('');
    setProjectRepSignature(null);
    setQaSignature(null);
  }, [isOpen, isFetchingDraft, draft]);

  useEffect(() => {
    if (saveDraftSuccess && isOpen && numericProjectId) {
      clearDraft();
      loadDraft({
        projectId: numericProjectId,
        reportType: CONFORMITY_DRAFT_REPORT_TYPE
      });
    }
  }, [saveDraftSuccess, isOpen, numericProjectId, loadDraft, clearDraft]);

  useEffect(() => {
    if (!teamMembers?.length) return;
    const members = teamMembers as any[];
    const managers = members
      .filter(
        (m) =>
          m?.team_member_role === 'Project Manager' ||
          m?.team_member_role === 'Manager' ||
          m?.projectLevelRole?.project_role_name === 'Manager' ||
          m?.projectLevelRole?.project_role_name === 'Project Manager'
      )
      .map((m) => ({
        value: String(m.team_member_id),
        name: `${m.user?.first_name ?? ''} ${m.user?.last_name ?? ''}`.trim(),
        label: `${m.user?.first_name ?? ''} ${m.user?.last_name ?? ''} (${m.user?.email ?? ''})`.trim()
      }));
    const qc = members
      .filter(
        (m) =>
          m?.team_member_role === 'Quality Control' ||
          m?.team_member_role === 'QC' ||
          m?.team_member_role === 'QA/QC' ||
          m?.team_member_role === 'Senior QC Inspector'
      )
      .map((m) => ({
        value: String(m.team_member_id),
        name: `${m.user?.first_name ?? ''} ${m.user?.last_name ?? ''}`.trim(),
        label: `${m.user?.first_name ?? ''} ${m.user?.last_name ?? ''} (${m.user?.email ?? ''})`.trim()
      }));
    setRepList(managers.length ? managers : qc);
    setQaList(qc.length ? qc : managers);
  }, [teamMembers]);

  useEffect(() => {
    if (!isOpen || !draft || isFetchingDraft) return;

    isApplyingDraftRef.current = true;

    const d = draft as Record<string, unknown>;
    if (d.headerData && typeof d.headerData === 'object') {
      setHeaderData((prev) => ({ ...prev, ...(d.headerData as object) }));
    }
    if (typeof d.covering === 'string') setCovering(d.covering);
    if (typeof d.certifiesStatement === 'string') setCertifiesStatement(d.certifiesStatement);
    if (typeof d.exceptions === 'string') setExceptions(d.exceptions);
    if (Array.isArray(d.rows) && d.rows.length) {
      setRows(normalizeDraftRows(d.rows));
    }

    if (draftSignatures) {
      const pm = draftSignatures.projectManager as
        | { signed?: boolean; signature?: string; name?: string; comment?: string; comments?: string; stampImage?: string; timestamp?: string }
        | undefined;
      if (pm?.signed && pm.signature) {
        setProjectRepSignature({
          signature: pm.signature || '',
          name: pm.name || '',
          timestamp: pm.timestamp || new Date().toISOString(),
          comments: (pm.comment ?? pm.comments ?? '') as string,
          stampImage: pm.stampImage || ''
        });
      }
      const qc = draftSignatures.qc as
        | { signed?: boolean; signature?: string; name?: string; comment?: string; comments?: string; stampImage?: string; timestamp?: string }
        | undefined;
      if (qc?.signed && qc.signature) {
        setQaSignature({
          signature: qc.signature || '',
          name: qc.name || '',
          timestamp: qc.timestamp || new Date().toISOString(),
          comments: (qc.comment ?? qc.comments ?? '') as string,
          stampImage: qc.stampImage || ''
        });
      }
    }

    setTimeout(() => {
      isApplyingDraftRef.current = false;
    }, 0);
  }, [isOpen, draft, draftSignatures, isFetchingDraft]);

  useEffect(() => {
    if (!isOpen || !draft || isFetchingDraft || repList.length === 0 || qaList.length === 0) return;
    const d = draft as Record<string, unknown>;
    const pmSig = draftSignatures?.projectManager as { teamMemberId?: string | number } | undefined;
    const qcSig = draftSignatures?.qc as { teamMemberId?: string | number } | undefined;

    const restoreKey = `${String(draftId ?? '')}-${repList.length}-${qaList.length}`;
    if (lastSignerRestoreKeyRef.current === restoreKey) return;
    lastSignerRestoreKeyRef.current = restoreKey;

    const repId =
      typeof d.selectedRep === 'string' && d.selectedRep && repList.some((m) => m.value === d.selectedRep)
        ? d.selectedRep
        : pmSig?.teamMemberId != null && String(pmSig.teamMemberId) !== '' && repList.some((m) => m.value === String(pmSig.teamMemberId))
          ? String(pmSig.teamMemberId)
          : '';
    if (repId) setSelectedRep(repId);

    const qaId =
      typeof d.selectedQa === 'string' && d.selectedQa && qaList.some((m) => m.value === d.selectedQa)
        ? d.selectedQa
        : qcSig?.teamMemberId != null && String(qcSig.teamMemberId) !== '' && qaList.some((m) => m.value === String(qcSig.teamMemberId))
          ? String(qcSig.teamMemberId)
          : '';
    if (qaId) setSelectedQa(qaId);
  }, [isOpen, draft, draftSignatures, draftId, isFetchingDraft, repList, qaList]);

  const handleHeaderChange = (field: keyof typeof headerData, value: string) => {
    setHeaderData((prev) => ({ ...prev, [field]: value }));
  };

  const updateRow = (id: string, patch: Partial<ConformityProcessRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const addRow = () => {
    setRows((prev) => [...prev, { id: newRowId(), description: '' }]);
  };

  const removeRow = (id: string) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)));
  };

  const handleSaveDraft = useCallback(() => {
    if (!numericProjectId) return;

    const formData = {
      headerData,
      covering,
      certifiesStatement,
      exceptions,
      rows,
      selectedRep,
      selectedQa
    };

    const signatures: Record<string, unknown> = {
      projectManager: projectRepSignature?.signature
        ? {
            name: projectRepSignature.name || '',
            signature: projectRepSignature.signature || '',
            stampImage: projectRepSignature.stampImage || '',
            timestamp: projectRepSignature.timestamp || new Date().toISOString(),
            signed: true,
            comment: projectRepSignature.comments || '',
            teamMemberId: selectedRep || null
          }
        : { signed: false },
      qc: qaSignature?.signature
        ? {
            name: qaSignature.name || '',
            signature: qaSignature.signature || '',
            stampImage: qaSignature.stampImage || '',
            timestamp: qaSignature.timestamp || new Date().toISOString(),
            signed: true,
            comment: qaSignature.comments || '',
            teamMemberId: selectedQa || null
          }
        : { signed: false }
    };

    const saveDraftPayload: Record<string, unknown> = {
      projectId: numericProjectId,
      reportType: CONFORMITY_DRAFT_REPORT_TYPE,
      formData,
      signatures
    };

    if (draftId) {
      saveDraftPayload.draftId = draftId;
    }

    saveDraft(saveDraftPayload);
  }, [
    numericProjectId,
    headerData,
    covering,
    certifiesStatement,
    exceptions,
    rows,
    selectedRep,
    selectedQa,
    projectRepSignature,
    qaSignature,
    draftId,
    saveDraft
  ]);

  const openSignature = (ctx: 'rep' | 'qa') => {
    setSignatureContext(ctx);
    setIsSignatureDialogOpen(true);
  };

  const handleSignatureSave = (signature: string, name: string, comment?: string, stampImage?: string) => {
    const data: FinalSignature = {
      signature,
      name,
      timestamp: new Date().toISOString(),
      comments: comment || '',
      stampImage: stampImage || ''
    };
    if (signatureContext === 'rep') setProjectRepSignature(data);
    if (signatureContext === 'qa') setQaSignature(data);
    setIsSignatureDialogOpen(false);
    setSignatureContext(null);
  };

  const signatureInitialName = useMemo(() => {
    if (signatureContext === 'rep') {
      return repList.find((m) => m.value === selectedRep)?.name || '';
    }
    if (signatureContext === 'qa') {
      return qaList.find((m) => m.value === selectedQa)?.name || '';
    }
    return '';
  }, [signatureContext, repList, qaList, selectedRep, selectedQa]);

  const signatureDialogInitialSignature = useMemo(() => {
    if (signatureContext === 'rep') return projectRepSignature?.signature?.trim() ? projectRepSignature.signature : null;
    if (signatureContext === 'qa') return qaSignature?.signature?.trim() ? qaSignature.signature : null;
    return null;
  }, [signatureContext, projectRepSignature?.signature, qaSignature?.signature]);

  const signatureDialogInitialStamp = useMemo(() => {
    if (signatureContext === 'rep') return projectRepSignature?.stampImage?.trim() ? projectRepSignature.stampImage : null;
    if (signatureContext === 'qa') return qaSignature?.stampImage?.trim() ? qaSignature.stampImage : null;
    return null;
  }, [signatureContext, projectRepSignature?.stampImage, qaSignature?.stampImage]);

  const formatDateTime = (iso: string) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short'
      });
    } catch {
      return iso;
    }
  };

  const formatSigDate = (iso: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    const day = d.getDate().toString().padStart(2, '0');
    const month = d.toLocaleString('en-US', { month: 'short' });
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const buildPayload = (): ConformityReportFormPayload => ({
    reportType: CONFORMITY_DRAFT_REPORT_TYPE,
    projectName,
    projectNumber: jobNumber,
    projectId,
    header: { ...headerData },
    covering,
    certifiesStatement,
    exceptions,
    rows,
    signatures: {
      projectRepresentative: projectRepSignature,
      qaSignatory: qaSignature
    },
    selectedSigners: {
      projectRepresentativeMemberId: selectedRep,
      qaMemberId: selectedQa
    }
  });

  const bothSignaturesReady = useMemo(
    () =>
      Boolean(projectRepSignature?.signature?.trim()) && Boolean(qaSignature?.signature?.trim()),
    [projectRepSignature?.signature, qaSignature?.signature]
  );

  const validate = (): string | null => {
    if (!headerData.clientName?.trim()) return 'Client name is required.';
    if (!headerData.clientPurchaseOrderNo?.trim()) return 'Client purchase order / contract number is required.';
    if (!selectedRep) return 'Select a project representative before signing.';
    if (!selectedQa) return 'Select a QA / QC signatory before signing.';
    if (!projectRepSignature?.signature?.trim()) return 'Project representative signature is required.';
    if (!qaSignature?.signature?.trim()) return 'QA / QC signatory signature is required.';
    return null;
  };

  const closeViewer = useCallback(() => {
    setViewerFile(null);
    dispatch(clearMaterialRegistryReport());
  }, [dispatch]);

  const handleGenerate = () => {
    const err = validate();
    if (err) {
      customNotification.error('Validation Error', err);
      return;
    }
    const payload = buildPayload();
    dispatch(generateMaterialRegistryReport(payload));
  };

 

  const textareaClass =
    'flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

  if (viewerFile) {
    return (
      <PdfViewer fileUrl={viewerFile.fileUrl} fileName={viewerFile.fileName} onClose={closeViewer} />
    );
  }

  const handleClose = () => {
    closeViewer();
    onClose();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="w-screen h-screen max-w-none m-0 p-0 gap-0 border-0 bg-white shadow-none rounded-none [&>button]:hidden">
          <div className="flex flex-col relative z-20 h-screen overflow-hidden">
            <DialogHeader className="px-4 sm:px-6 py-4 border-b flex flex-row items-center justify-between bg-white relative z-10 flex-shrink-0 gap-2 flex-wrap">
              <div className="flex items-center gap-4 min-w-0">
                <Button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" variant="outline">
                  <ArrowLeft className="h-5 w-5 text-gray-500" />
                </Button>
                <div className="min-w-0">
                  <DialogTitle className="text-lg font-bold text-gray-900">Generate Conformity Report</DialogTitle>
                  {projectName ? <p className="text-sm text-gray-600 mt-1 truncate">Conformity Report: {projectName}</p> : null}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
               
              </div>
            </DialogHeader>

            <div className="p-4 sm:p-6 lg:p-8 overflow-y-auto flex-1 relative">
              <div className="space-y-8 max-w-full mx-auto">
                {isFetchingDraft ? (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading draft…
                  </div>
                ) : null}
                {updatedAt && !isFetchingDraft ? (
                  <p className="text-xs text-gray-500">Draft last updated {formatDateTime(updatedAt)}</p>
                ) : null}
                {fetchDraftError ? (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                    <AlertCircle className="h-5 w-5 flex-shrink-0" />
                    <span>Could not load draft: {fetchDraftError}</span>
                  </div>
                ) : null}
                {saveDraftSuccess ? (
                  <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                    <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                    <span>Draft saved successfully.</span>
                  </div>
                ) : null}
                {saveDraftError ? (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                    <AlertCircle className="h-5 w-5 flex-shrink-0" />
                    <span>Failed to save draft: {saveDraftError}</span>
                  </div>
                ) : null}

                <section className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Report Header</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="conformityReportNo">Conformity Report No.</Label>
                      <Input
                        id="conformityReportNo"
                        value={headerData.conformityReportNo}
                        onChange={(e) => handleHeaderChange('conformityReportNo', e.target.value)}
                        placeholder="Optional reference"
                      />
                    </div>
                    {/* <div className="space-y-2">
                      <Label htmlFor="issuerCompany">For and on behalf of</Label>
                      <Input
                        id="issuerCompany"
                        value={headerData.issuerCompany}
                        onChange={(e) => handleHeaderChange('issuerCompany', e.target.value)}
                        placeholder="Company name"
                      />
                    </div> */}
                    <div className="space-y-2">
                      <Label htmlFor="clientName">
                        Client Name
                      </Label>
                      <Input
                        id="clientName"
                        value={headerData.clientName}
                        onChange={(e) => handleHeaderChange('clientName', e.target.value)}
                        placeholder="Client name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="clientPurchaseOrderNo">
                        Client purchase order / contract number
                      </Label>
                      <Input
                        id="clientPurchaseOrderNo"
                        value={headerData.clientPurchaseOrderNo}
                        onChange={(e) => handleHeaderChange('clientPurchaseOrderNo', e.target.value)}
                        placeholder="PO or contract number"
                      />
                    </div>
                  </div>
                </section>

                <section className="space-y-2">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Covering Statement</h3>
                  <textarea id="covering" className={textareaClass} value={covering} onChange={(e) => setCovering(e.target.value)} />
                </section>

                <section className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b pb-2">
                    <h3 className="text-lg font-semibold text-gray-900"> Scope</h3>
                    <Button type="button"  size="sm" className="gap-1 w-fit" onClick={addRow}>
                      <Plus className="h-4 w-4" />
                      Add Scope
                    </Button>
                  </div>
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b text-left">
                          <th className="p-2 font-medium">Description</th>
                          <th className="p-2 w-12" />
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row) => (
                          <tr key={row.id} className="border-b last:border-0 align-top">
                            <td className="p-2">
                              <textarea
                                className={cn(textareaClass, 'min-h-[72px]')}
                                value={row.description}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  updateRow(row.id, { description: v });
                                }}
                              />
                            </td>
                            <td className="p-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-red-600 h-8 w-8 p-0"
                                onClick={() => removeRow(row.id)}
                                title="Remove row"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="space-y-2">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Certification statement</h3>
                  <textarea
                    className={textareaClass}
                    value={certifiesStatement}
                    onChange={(e) => setCertifiesStatement(e.target.value)}
                  />
                </section>

                <section className="space-y-2">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Exceptions</h3>
                  <textarea className={textareaClass} value={exceptions} onChange={(e) => setExceptions(e.target.value)} />
                </section>

                <section className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Signatures</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="border rounded-lg p-4 space-y-3">
                      <Label>Project representative</Label>
                      <div className="flex flex-col gap-2">
                        <div className="flex flex-row items-center gap-2 w-full min-w-0">
                          <div className="flex-1 min-w-0">
                            <Select
                              value={selectedRep || undefined}
                              onValueChange={(v) => {
                                setSelectedRep(v);
                                if (!isApplyingDraftRef.current) setProjectRepSignature(null);
                              }}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select project representative" />
                              </SelectTrigger>
                              <SelectContent>
                                {repList.length ? (
                                  repList.map((m) => (
                                    <SelectItem key={m.value} value={m.value}>
                                      {m.label}
                                    </SelectItem>
                                  ))
                                ) : (
                                  <SelectItem value="_none" disabled>
                                    No team members loaded
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                          <Button
                            type="button"
                            className="gap-2 shrink-0 whitespace-nowrap bg-[#e04530] hover:bg-[#e04530]/90 text-white border-0 shadow-sm"
                            disabled={!selectedRep}
                            onClick={() => openSignature('rep')}
                          >
                            <FileSignature className="h-4 w-4" />
                            {projectRepSignature?.signature ? 'Update signature' : 'Add signature'}
                          </Button>
                          {projectRepSignature?.signature ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-9 w-9 shrink-0 border-green-200 text-green-700 bg-green-50"
                              disabled
                              title="Signed"
                            >
                              <CheckCircle2 className="h-4 w-4" />
                            </Button>
                          ) : null}
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-9 w-9 shrink-0 text-red-600 border-gray-200 disabled:opacity-40"
                            disabled={!projectRepSignature?.signature}
                            onClick={() => setProjectRepSignature(null)}
                            title="Remove signature"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        {projectRepSignature?.signature ? (
                          <div className="rounded-md border bg-gray-50 p-3 space-y-1 text-sm">
                            <div className="flex justify-between gap-2">
                              <span className="text-gray-500">Signed by</span>
                              <span className="font-medium">{projectRepSignature.name}</span>
                            </div>
                            <div className="flex justify-between gap-2">
                              <span className="text-gray-500">Date</span>
                              <span>{formatSigDate(projectRepSignature.timestamp)}</span>
                            </div>
                            <img src={projectRepSignature.signature} alt="Signature" className="max-h-16 mt-2" />
                            {projectRepSignature.stampImage?.trim() ? (
                              <div className="mt-3 pt-2 border-t border-gray-200">
                                <span className="text-gray-500 text-xs block mb-1">Stamp</span>
                                <img src={projectRepSignature.stampImage} alt="Stamp" className="max-h-20 object-contain" />
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="border rounded-lg p-4 space-y-3">
                      <Label>QA / QC Inspector</Label>
                      <div className="flex flex-col gap-2">
                        <div className="flex flex-row items-center gap-2 w-full min-w-0">
                          <div className="flex-1 min-w-0">
                            <Select
                              value={selectedQa || undefined}
                              onValueChange={(v) => {
                                setSelectedQa(v);
                                if (!isApplyingDraftRef.current) setQaSignature(null);
                              }}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select QA / QC signatory" />
                              </SelectTrigger>
                              <SelectContent>
                                {qaList.length ? (
                                  qaList.map((m) => (
                                    <SelectItem key={m.value} value={m.value}>
                                      {m.label}
                                    </SelectItem>
                                  ))
                                ) : (
                                  <SelectItem value="_none2" disabled>
                                    No team members loaded
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                          <Button
                            type="button"
                            className="gap-2 shrink-0 whitespace-nowrap bg-[#e04530] hover:bg-[#e04530]/90 text-white border-0 shadow-sm"
                            disabled={!selectedQa}
                            onClick={() => openSignature('qa')}
                          >
                            <FileSignature className="h-4 w-4" />
                            {qaSignature?.signature ? 'Update signature' : 'Add signature'}
                          </Button>
                          {qaSignature?.signature ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-9 w-9 shrink-0 border-green-200 text-green-700 bg-green-50"
                              disabled
                              title="Signed"
                            >
                              <CheckCircle2 className="h-4 w-4" />
                            </Button>
                          ) : null}
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-9 w-9 shrink-0 text-red-600 border-gray-200 disabled:opacity-40"
                            disabled={!qaSignature?.signature}
                            onClick={() => setQaSignature(null)}
                            title="Remove signature"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        {qaSignature?.signature ? (
                          <div className="rounded-md border bg-gray-50 p-3 space-y-1 text-sm">
                            <div className="flex justify-between gap-2">
                              <span className="text-gray-500">Signed by</span>
                              <span className="font-medium">{qaSignature.name}</span>
                            </div>
                            <div className="flex justify-between gap-2">
                              <span className="text-gray-500">Date</span>
                              <span>{formatSigDate(qaSignature.timestamp)}</span>
                            </div>
                            <img src={qaSignature.signature} alt="Signature" className="max-h-16 mt-2" />
                            {qaSignature.stampImage?.trim() ? (
                              <div className="mt-3 pt-2 border-t border-gray-200">
                                <span className="text-gray-500 text-xs block mb-1">Stamp</span>
                                <img src={qaSignature.stampImage} alt="Stamp" className="max-h-20 object-contain" />
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </section>

                {materialRegistryReportError ? (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                    <AlertCircle className="h-5 w-5 flex-shrink-0" />
                    <span>Failed to generate report: {materialRegistryReportError}</span>
                  </div>
                ) : null}

                <div className="flex flex-wrap justify-end gap-2 pb-8">
                  <Button
                    type="button"
                    variant="outline"
                    className="gap-2"
                    onClick={handleSaveDraft}
                    disabled={isSavingDraft || !numericProjectId || isFetchingDraft}
                  >
                    {isSavingDraft ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Saving…
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        Save draft
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleGenerate}
                    disabled={materialRegistryReportLoading || !bothSignaturesReady}
                    className="bg-[#e04530] hover:bg-[#e04530]/90 text-white px-8 disabled:opacity-50 disabled:pointer-events-none"
                    size="lg"
                    title={
                      bothSignaturesReady
                        ? undefined
                        : 'Add both project representative and QA / QC signatures to generate the report.'
                    }
                  >
                    {materialRegistryReportLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <FileText className="h-4 w-4 mr-2" />
                        Generate report
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      

      <SignatureDialog
        isOpen={isSignatureDialogOpen}
        onClose={() => {
          setIsSignatureDialogOpen(false);
          setSignatureContext(null);
        }}
        onSave={handleSignatureSave}
        title={signatureContext === 'rep' ? 'Project representative signature' : 'QA / QC signatory signature'}
        initialName={signatureInitialName}
        initialSignature={signatureDialogInitialSignature}
        initialStampImage={signatureDialogInitialStamp}
      />
    </>
  );
};
